import type { NavigateFunction } from "react-router-dom";

export interface StorefrontNavigationState {
  scrollTarget?: string;
  scrollToTop?: boolean;
}

const PREVIEW_PARAM_PREFIX = "preview_";

export const getPreservedStorefrontSearch = (search: string) => {
  const current = new URLSearchParams(search);
  const preserved = new URLSearchParams();

  current.forEach((value, key) => {
    if (key.startsWith(PREVIEW_PARAM_PREFIX)) {
      preserved.append(key, value);
    }
  });

  const next = preserved.toString();
  return next ? `?${next}` : "";
};

export const mergePreservedPreviewParams = (path: string, currentSearch: string) => {
  if (!path.startsWith("/")) return path;

  const [pathnameWithSearch, hash = ""] = path.split("#");
  const [pathname, rawSearch = ""] = pathnameWithSearch.split("?");
  const targetParams = new URLSearchParams(rawSearch);
  const currentParams = new URLSearchParams(currentSearch);

  currentParams.forEach((value, key) => {
    if (key.startsWith(PREVIEW_PARAM_PREFIX) && !targetParams.has(key)) {
      targetParams.set(key, value);
    }
  });

  const nextSearch = targetParams.toString();
  const nextHash = hash ? `#${hash}` : "";
  return `${pathname}${nextSearch ? `?${nextSearch}` : ""}${nextHash}`;
};

export const buildStorefrontHomePath = (search: string, hash?: string) => {
  const preservedSearch = getPreservedStorefrontSearch(search);
  return `/${preservedSearch}${hash ? `#${hash}` : ""}`;
};

export const scrollToStorefrontTarget = (target?: string | null) => {
  if (!target || target === "home") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  let attempts = 0;
  const maxAttempts = 20;

  const tryScroll = () => {
    const element = document.getElementById(target);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (attempts < maxAttempts) {
      attempts += 1;
      window.setTimeout(tryScroll, 50);
    }
  };

  tryScroll();
};

export const navigateToStorefrontHome = (
  navigate: NavigateFunction,
  currentSearch: string,
  options?: StorefrontNavigationState
) => {
  navigate(buildStorefrontHomePath(currentSearch), { state: options });
};